import config from "config/config";
import { Attachment } from "models/dashboard/dashboard";
import { MessageType } from "store/slices/inboxSlice";
import { apiRequest } from "./apiInterceptor";

type QueryParams = { skip: number; limit: number; searchCriteria?: string };

export class InboxAPI {
  static fetchThread = async (customerId: string, threadId: string) => {
    const response = await apiRequest
      .get(
        config.api.customer.thread.get +
          customerId +
          "/thread/" +
          encodeURIComponent(threadId)
      );

    return response.body;
  };

  static fetchThreads = async (
    customerId: string,
    getAllThreads = false,
    skip = 0,
    searchTerms = "",
    queryParams?: string
  ) => {
    let queryParam: QueryParams = {
      skip: skip,
      limit: config.defaults.pageSize
    };
    if (searchTerms !== "") {
      queryParam = { ...queryParam, searchCriteria: searchTerms };
    }

    if (queryParams) {
      const urlParams = new URLSearchParams(queryParams);
      urlParams.forEach((value, key) => {
        queryParam[key] = value;
      });
    }

    const response = await apiRequest
      .get(
        config.api.customer.threads.get +
          customerId +
          "/threads" +
          (getAllThreads ? "/All" : "")
      )
      .query(queryParam);

    return response.body;
  };

  static fetchMoreThreads = async (
    customerId: string,
    getAllThreads: boolean,
    skip: number,
    searchTerms?: string,
    queryParams?: string
  ) => {
    let queryParam: QueryParams = {
      skip: skip,
      limit: config.defaults.pageSize
    };
    if (searchTerms !== "") {
      queryParam = { ...queryParam, searchCriteria: searchTerms };
    }

    const response = await apiRequest
      .get(
        config.api.customer.threads.get +
          customerId +
          "/threads" +
          (getAllThreads ? "/All" : "") +
          (queryParams ? `?${queryParams}` : "")
      )
      .query(queryParam);

    return response.body;
  };

  static setThreadStatus = async (
    customerId: string,
    threadId: string,
    action: string
  ) => {
    const response = await apiRequest
      .put(config.api.customer.thread.put(customerId, threadId))
      .send({
        Action: action
      });

    return response;
  };

  static fetchSentMessages = async (customerId: string) => {
    const response = await apiRequest
      .get(config.api.message.sentMessage(customerId));

    return response.body;
  };

  static sendMessage = async (
    fromUserId: string,
    body: string,
    title: string,
    threadId: string | null,
    subscribers: string[],
    customerId: string,
    attachments: Attachment[],
    type = MessageType.Normal,
    reportStart = "",
    reportEnd = "",
    category?: number
  ) => {
    const response = await apiRequest
      .post(config.api.message.post)
      .send({
        From: fromUserId,
        Body: body,
        Title: title,
        ThreadId: threadId,
        Subscribers: subscribers,
        CustomerId: customerId,
        Attachments: attachments,
        Type: type,
        ReportStartPeriod: reportStart,
        ReportEndPeriod: reportEnd,
        ThreadCategory: category
      });

    return response.body;
  };

  static archiveThread = async (customerId: string, threadId: string) => {
    const response = await apiRequest
      .put(config.api.messages.archive(customerId, threadId));
    return response.body;
  };

  static unarchiveThread = async (customerId: string, threadId: string) => {
    const response = await apiRequest
      .put(config.api.messages.unarchive(customerId, threadId));
    return response.body;
  };

  static hideThreadMessage = async (customerId: string, messageId: string) => {
    const response = await apiRequest
      .put(config.api.message.hide(customerId, messageId));

    return response.body;
  };

  static unhideThreadMessage = async (
    customerId: string,
    messageId: string
  ) => {
    const response = await apiRequest
      .put(config.api.message.unhide(customerId, messageId));

    return response.body;
  };

  static updateThreadCategory = async (
    customerId: string,
    threadId: string,
    category: number
  ) => {
    const response = await apiRequest
      .put(
        `${config.api.customer.thread.get}${customerId}/thread/${encodeURIComponent(
          threadId
        )}/category`
      )
      .send({
        Category: category
      });

    return response.body;
  };
}
